import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { auditService } from '../../services/auditService';
import { fmtQty } from '../Reports/ui';
import DataGrid, { dash, numberColumn } from '../../components/DataGrid';
import { PageLoader } from '../../components/Loader';

const keyOf = (row) => `${row.ean}|${row.location}`;

function when(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

/**
 * Correct-EAN cell of an invalid row: the store team types the correct EAN,
 * which is checked against the audit's AMD master as they type; Save is
 * enabled once it matches.
 */
function CorrectionCell({ auditId, row, onSaved }) {
  const [value, setValue] = useState('');
  const [check, setCheck] = useState(null); // null | 'checking' | { valid, product }
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const latest = useRef('');

  useEffect(() => {
    const code = value.trim();
    latest.current = code;
    setError('');
    if (!code) {
      setCheck(null);
      return undefined;
    }
    setCheck('checking');
    const timer = setTimeout(async () => {
      try {
        const result = await auditService.lookupEan(auditId, code);
        if (latest.current === code) setCheck(result);
      } catch (err) {
        if (latest.current === code) {
          setCheck(null);
          setError(err.message);
        }
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [value, auditId]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await auditService.correctEan(auditId, { ean: row.ean, location: row.location, new_ean: value.trim() });
      onSaved();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const valid = check && check !== 'checking' && check.valid;
  const invalid = check && check !== 'checking' && !check.valid;

  return (
    <div className={`valid-ean__cell${valid ? ' valid-ean__cell--ok' : invalid ? ' valid-ean__cell--bad' : ''}`}>
      <div className="valid-ean__input">
        <input
          value={value}
          onChange={(event) => setValue(event.target.value.replace(/\s/g, ''))}
          onKeyDown={(event) => {
            // Keep typing inside the cell (the grid would otherwise use the keys).
            event.stopPropagation();
            if (event.key === 'Enter' && valid && !saving) save();
          }}
          placeholder="Correct EAN"
          inputMode="numeric"
          aria-label={`Correct EAN for ${row.ean}${row.location ? ` at ${row.location}` : ''}`}
        />
        <button type="button" className="compare-button" disabled={!valid || saving} onClick={save}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <span className="valid-ean__status" aria-live="polite">
        {check === 'checking'
          ? 'Checking…'
          : valid
            ? `✓ ${check.product?.product_name || 'In AMD master'}`
            : invalid
              ? '✗ Invalid EAN — not in the AMD master'
              : ''}
      </span>
      {error ? <span className="valid-ean__error">{error}</span> : null}
    </div>
  );
}

/**
 * "Valid EAN" tab: invalid scans (EAN not in the AMD master), grouped by EAN
 * and location. Each gets a correct EAN from the master; corrected scans
 * join the valid data used by the comparisons and variance report.
 */
function ValidEanPanel({ auditId, canEdit, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  // Which rows to show: all, still invalid, or invalid resolved.
  const [show, setShow] = useState('all');

  const load = useCallback(async () => {
    try {
      setData(await auditService.getEanCorrections(auditId));
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const pending = useMemo(() => data?.pending || [], [data]);

  const undo = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Undo the correction ${row.original_ean} → ${row.ean}? These scans become invalid again.`)) return;
    setBusy(`${row.original_ean}|${row.ean}|${row.location}`);
    try {
      await auditService.undoEanCorrection(auditId, {
        original_ean: row.original_ean,
        ean: row.ean,
        location: row.location,
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const editable = canEdit && Boolean(data?.has_master);

  const pendingColumns = [
    {
      headerName: 'Scanned EAN (invalid)',
      field: 'ean',
      minWidth: 200,
      getQuickFilterText: (p) => `${p.data.ean} ${p.data.product_name || ''}`,
      cellRenderer: (p) => (
        <>
          <code>{p.data.ean}</code>
          {p.data.product_name ? <small className="live-muted"> {p.data.product_name}</small> : null}
        </>
      ),
    },
    { headerName: 'Location', field: 'location', valueFormatter: (p) => dash(p.value) },
    numberColumn('Qty', 'qty', fmtQty),
    numberColumn('Scans', 'scans', fmtQty),
    { headerName: 'Scanned by', field: 'people', valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Status',
      colId: 'status',
      maxWidth: 120,
      valueGetter: () => 'Invalid',
      cellRenderer: () => <span className="compare-pill compare-pill--invalid">Invalid</span>,
    },
    {
      headerName: 'Correct EAN',
      colId: 'correct',
      minWidth: 320,
      flex: 2,
      sortable: false,
      filter: false,
      autoHeight: true,
      cellRenderer: (p) =>
        editable ? (
          <CorrectionCell auditId={auditId} row={p.data} onSaved={load} />
        ) : (
          <span className="live-muted">Waiting for the store team (client login)</span>
        ),
    },
  ];

  const correctedColumns = [
    {
      headerName: 'Scanned as',
      field: 'original_ean',
      minWidth: 160,
      cellRenderer: (p) => <code>{p.value}</code>,
    },
    {
      headerName: 'Corrected to',
      field: 'ean',
      minWidth: 220,
      flex: 2,
      getQuickFilterText: (p) => `${p.data.ean} ${p.data.product_name || ''}`,
      cellRenderer: (p) => (
        <>
          <code>{p.data.ean}</code>
          {p.data.product_name ? <small className="live-muted"> {p.data.product_name}</small> : null}
        </>
      ),
    },
    { headerName: 'Location', field: 'location', valueFormatter: (p) => dash(p.value) },
    numberColumn('Qty', 'qty', fmtQty),
    {
      headerName: 'Status',
      colId: 'status',
      minWidth: 150,
      valueGetter: () => 'Invalid resolved',
      cellRenderer: () => <span className="compare-pill compare-pill--resolved">Invalid resolved</span>,
    },
    {
      headerName: 'Resolved by',
      field: 'corrected_by_name',
      minWidth: 220,
      getQuickFilterText: (p) => `${p.data.corrected_by_name || ''} ${when(p.data.corrected_at)}`,
      cellRenderer: (p) => (
        <>
          {p.data.corrected_by_name || '—'}
          <small className="live-muted"> {when(p.data.corrected_at)}</small>
        </>
      ),
    },
    ...(canEdit
      ? [
          {
            headerName: '',
            colId: 'undo',
            sortable: false,
            filter: false,
            maxWidth: 100,
            cellRenderer: (p) => {
              const id = `${p.data.original_ean}|${p.data.ean}|${p.data.location}`;
              return (
                <button type="button" className="compare-link" disabled={busy === id} onClick={() => undo(p.data)}>
                  Undo
                </button>
              );
            },
          },
        ]
      : []),
  ];

  if (!data) {
    return (
      <section className="audit-portal__panel live-panel">
        {error ? <p className="compare-error">{error}</p> : <PageLoader label="Loading invalid scans…" />}
      </section>
    );
  }

  const { summary } = data;

  return (
    <section className="audit-portal__panel live-panel">
      <header className="live-panel__header">
        <div>
          <h2>Valid EAN — correct invalid scans</h2>
          <p className="live-muted">
            These scans have an EAN that is not in the AMD master. The store team enters the correct
            EAN for each row in the client login; it is checked against the master as they type.
            Resolved scans are marked <strong className="valid-ean__green">Invalid resolved</strong>{' '}
            with who resolved them, count as valid and are included in the comparisons and variance
            report. Rows still <strong className="valid-ean__red">Invalid</strong> are not counted.
          </p>
          <label className="valid-ean__show">
            Show{' '}
            <select value={show} onChange={(event) => setShow(event.target.value)} aria-label="Show">
              <option value="all">All</option>
              <option value="invalid">Invalid (not resolved)</option>
              <option value="resolved">Invalid resolved</option>
            </select>
          </label>
        </div>
      </header>

      {error ? <p className="compare-error">{error}</p> : null}
      {!data.has_master ? (
        <p className="compare-error">
          No AMD master has been uploaded for this schedule yet, so EANs cannot be checked.
        </p>
      ) : null}

      <section className="compare-cards">
        <article className={`compare-card${summary.pending_rows ? ' compare-card--bad' : ' compare-card--good'}`}>
          <span>Invalid rows to correct</span>
          <strong>{fmtQty(summary.pending_rows)}</strong>
          <p>
            {fmtQty(summary.pending_scans)} scans · qty {fmtQty(summary.pending_qty)}
          </p>
        </article>
        <article className="compare-card compare-card--good">
          <span>Invalid resolved</span>
          <strong>{fmtQty(summary.corrected_rows)}</strong>
          <p>
            {fmtQty(summary.corrected_scans)} scans · qty {fmtQty(summary.corrected_qty)} now valid
          </p>
        </article>
      </section>

      {summary.all_corrected ? (
        <p className="live-notice">
          {summary.corrected_rows
            ? 'All invalid entries are corrected and included in the valid data and the variance report.'
            : 'There are no invalid scans in this schedule.'}
        </p>
      ) : null}

      {show !== 'resolved' && data.pending.length ? (
        <>
          <DataGrid
            rowData={pending}
            columnDefs={pendingColumns}
            getRowId={keyOf}
            pageSize={20}
            searchPlaceholder="Search EAN, location or person…"
            emptyMessage="No invalid scans."
          />
        </>
      ) : null}

      {show !== 'invalid' && data.corrected.length ? (
        <>
          <h3 className="live-subhead">Invalid resolved ({fmtQty(data.corrected.length)})</h3>
          <DataGrid
            rowData={data.corrected}
            columnDefs={correctedColumns}
            getRowId={(row) => `${row.original_ean}|${row.ean}|${row.location}`}
            pageSize={20}
            searchPlaceholder="Search EAN, location or person…"
          />
        </>
      ) : null}
    </section>
  );
}

export default ValidEanPanel;
